import { CollectionScope, CONNECTION_RESUMED_EVENT, ListResource, RealTimeEvent } from '@nemuru-eng/js-api-client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useRealTimeResource } from '@/hooks/useRealTimeResource';

export type LiveUpdatesProps = { resource: ListResource; scope?: CollectionScope };

const SILENT_REFETCH_DELAY_MS = 1500;
const PENDING_IDS_CAP = 99;

interface UseLiveListOptions {
  liveUpdates?: LiveUpdatesProps;
  rowIds: string[];
  refetch: () => void;
}

/**
 * Real-time behaviour of a server-side paginated list: a change to a row that is on screen (or a
 * reconnection) re-fetches the page silently; a change to any other item only bumps a pending counter
 * that the toolbar shows as "N cambios · Actualizar". No extra request is needed to count.
 *
 * Off-screen misses are accumulated in a ref and flushed to state through the same 1.5 s coalescing
 * timer as the silent refetch, so a burst of events causes at most one re-render per 1.5 s.
 */
export const useLiveList = ({ liveUpdates, rowIds, refetch }: UseLiveListOptions) => {
  const pendingRef = useRef(new Set<string>());
  const [pendingCount, setPendingCount] = useState(0);
  const rowIdsRef = useRef(rowIds);
  rowIdsRef.current = rowIds;
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const refetchTimer = useRef<ReturnType<typeof setTimeout>>();
  const flushTimer = useRef<ReturnType<typeof setTimeout>>();

  const scheduleSilentRefetch = useCallback(() => {
    if (refetchTimer.current) return;
    refetchTimer.current = setTimeout(() => {
      refetchTimer.current = undefined;
      refetchRef.current();
    }, SILENT_REFETCH_DELAY_MS);
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current) return;
    flushTimer.current = setTimeout(() => {
      flushTimer.current = undefined;
      setPendingCount(pendingRef.current.size);
    }, SILENT_REFETCH_DELAY_MS);
  }, []);

  useEffect(
    () => () => {
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
      if (flushTimer.current) clearTimeout(flushTimer.current);
    },
    [],
  );

  const { state } = useRealTimeResource({
    resource: liveUpdates?.resource,
    id: liveUpdates?.scope,
    enabled: Boolean(liveUpdates?.resource && liveUpdates?.scope),
    onEvent: (event: RealTimeEvent) => {
      if (event.event === CONNECTION_RESUMED_EVENT || rowIdsRef.current.includes(event.id)) {
        scheduleSilentRefetch();
        return;
      }
      if (pendingRef.current.size < PENDING_IDS_CAP) {
        pendingRef.current.add(event.id);
        scheduleFlush();
      }
    },
  });

  const clearPendingChanges = useCallback(() => {
    if (refetchTimer.current) {
      clearTimeout(refetchTimer.current);
      refetchTimer.current = undefined;
    }
    // A queued count flush is simply dropped; the ref and state are cleared right away.
    if (flushTimer.current) {
      clearTimeout(flushTimer.current);
      flushTimer.current = undefined;
    }
    pendingRef.current = new Set();
    setPendingCount((count) => (count ? 0 : count));
  }, []);

  const applyPendingChanges = useCallback(() => {
    clearPendingChanges();
    refetchRef.current();
  }, [clearPendingChanges]);

  useEffect(() => {
    clearPendingChanges();
  }, [liveUpdates?.resource, liveUpdates?.scope, clearPendingChanges]);

  return {
    state: liveUpdates ? state : undefined,
    pendingChanges: pendingCount,
    applyPendingChanges,
    clearPendingChanges,
  };
};
